import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  CreditCard, Search, Edit, RefreshCw, Store, Calendar, 
  Sparkles, Plus, Minus, RotateCcw, Clock, CheckCircle, 
  XCircle, AlertCircle, ChevronDown, ChevronUp, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from 'sonner';

const planColors = {
  starter: 'bg-blue-100 text-blue-700',
  professional: 'bg-purple-100 text-purple-700',
  enterprise: 'bg-amber-100 text-amber-700',
  demo: 'bg-green-100 text-green-700',
};

const statusColors = {
  active: 'bg-green-100 text-green-700',
  trial: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
  expired: 'bg-slate-100 text-slate-700',
};

const statusIcons = {
  active: CheckCircle,
  trial: Clock,
  cancelled: XCircle,
  expired: AlertCircle,
};

export default function SubscriptionManagement() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [restaurants, setRestaurants] = useState([]);
  const [plans, setPlans] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Dialogs
  const [editDialog, setEditDialog] = useState({ open: false, restaurant: null });
  const [creditsDialog, setCreditsDialog] = useState({ open: false, restaurant: null });
  const [extendDialog, setExtendDialog] = useState({ open: false, restaurant: null });
  
  // Form states
  const [editForm, setEditForm] = useState({ plan: '', status: '', expires_at: '', notes: '' });
  const [creditsForm, setCreditsForm] = useState({ action: 'add', amount: 0, reason: '' });
  const [extendDays, setExtendDays] = useState(30);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/subscriptions');
      setRestaurants(response.data.restaurants || []);
      setPlans(response.data.plans || {});
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (restaurant) => {
    const sub = restaurant.subscription || {};
    setEditForm({
      plan: sub.plan || 'starter',
      status: sub.status || 'trial',
      expires_at: sub.expires_at ? sub.expires_at.split('T')[0] : '',
      notes: sub.notes || ''
    });
    setEditDialog({ open: true, restaurant });
  };

  const openCreditsDialog = (restaurant) => {
    setCreditsForm({ action: 'add', amount: 10, reason: '' });
    setCreditsDialog({ open: true, restaurant });
  };

  const openExtendDialog = (restaurant) => {
    setExtendDays(30);
    setExtendDialog({ open: true, restaurant });
  };

  const handleUpdateSubscription = async () => {
    if (!editDialog.restaurant) return;
    
    try {
      await api.put(`/admin/subscriptions/${editDialog.restaurant.id}`, {
        plan: editForm.plan,
        status: editForm.status,
        expires_at: editForm.expires_at ? new Date(editForm.expires_at).toISOString() : null,
        notes: editForm.notes
      });
      toast.success('Subscription updated successfully');
      setEditDialog({ open: false, restaurant: null });
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleUpdateCredits = async () => {
    if (!creditsDialog.restaurant) return;
    
    try {
      await api.put(`/admin/ai-credits/${creditsDialog.restaurant.id}`, {
        action: creditsForm.action,
        amount: parseInt(creditsForm.amount),
        reason: creditsForm.reason
      });
      toast.success(`AI credits ${creditsForm.action}ed successfully`);
      setCreditsDialog({ open: false, restaurant: null });
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to update AI credits');
    }
  };

  const handleResetCredits = async (restaurantId) => {
    try {
      await api.post(`/admin/ai-credits/${restaurantId}/reset`);
      toast.success('AI credits usage reset');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to reset credits');
    }
  };

  const handleExtendSubscription = async () => {
    if (!extendDialog.restaurant) return;
    
    try {
      await api.post(`/admin/subscriptions/${extendDialog.restaurant.id}/extend`, {
        days: parseInt(extendDays)
      });
      toast.success(`Subscription extended by ${extendDays} days`);
      setExtendDialog({ open: false, restaurant: null });
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to extend subscription');
    }
  };

  // Filter restaurants
  const filteredRestaurants = restaurants.filter(r => {
    const matchesSearch = 
      r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.owner?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const sub = r.subscription || {};
    const matchesPlan = planFilter === 'all' || sub.plan === planFilter;
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    
    return matchesSearch && matchesPlan && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getCreditsRemaining = (credits) => {
    if (!credits) return 0;
    const total = (credits.limit || 2) + (credits.purchased || 0);
    return total - (credits.used || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Subscription & Credits Management</h1>
          <p className="text-slate-500 mt-1">Manage restaurant subscriptions and AI credits</p>
        </div>
        <Button onClick={fetchSubscriptions} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{restaurants.filter(r => r.subscription?.status === 'active').length}</p>
              <p className="text-sm text-slate-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{restaurants.filter(r => r.subscription?.status === 'trial').length}</p>
              <p className="text-sm text-slate-500">Trial</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{restaurants.filter(r => r.subscription?.status === 'expired').length}</p>
              <p className="text-sm text-slate-500">Expired</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{restaurants.reduce((sum, r) => sum + getCreditsRemaining(r.ai_credits), 0)}</p>
              <p className="text-sm text-slate-500">Total Credits</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search restaurants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Plan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Plans</SelectItem>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
              <SelectItem value="demo">Demo</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Restaurants Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-4 font-medium text-slate-600">Restaurant</th>
              <th className="text-left p-4 font-medium text-slate-600">Plan</th>
              <th className="text-left p-4 font-medium text-slate-600">Status</th>
              <th className="text-left p-4 font-medium text-slate-600">Expires</th>
              <th className="text-left p-4 font-medium text-slate-600">AI Credits</th>
              <th className="text-right p-4 font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRestaurants.map((restaurant) => {
              const sub = restaurant.subscription || {};
              const credits = restaurant.ai_credits || {};
              const StatusIcon = statusIcons[sub.status] || AlertCircle;
              const remaining = getCreditsRemaining(credits);
              
              return (
                <tr key={restaurant.id} className="border-b hover:bg-slate-50">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                        <Store className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{restaurant.name}</p>
                        <p className="text-sm text-slate-500">{restaurant.owner?.name || restaurant.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${planColors[sub.plan] || 'bg-slate-100 text-slate-700'}`}>
                      {sub.plan ? sub.plan.charAt(0).toUpperCase() + sub.plan.slice(1) : 'None'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <StatusIcon className={`w-4 h-4 ${sub.status === 'active' ? 'text-green-500' : sub.status === 'trial' ? 'text-blue-500' : 'text-slate-400'}`} />
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[sub.status] || 'bg-slate-100 text-slate-700'}`}>
                        {sub.status ? sub.status.charAt(0).toUpperCase() + sub.status.slice(1) : 'Unknown'}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-slate-600">{formatDate(sub.expires_at)}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Sparkles className={`w-4 h-4 ${remaining > 0 ? 'text-purple-500' : 'text-slate-300'}`} />
                      <span className={`font-medium ${remaining > 0 ? 'text-slate-900' : 'text-red-500'}`}>
                        {remaining}
                      </span>
                      <span className="text-slate-400 text-sm">/ {(credits.limit || 2) + (credits.purchased || 0)}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(restaurant)}
                        title="Edit Subscription"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openExtendDialog(restaurant)}
                        title="Extend Subscription"
                      >
                        <Calendar className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openCreditsDialog(restaurant)}
                        title="Manage Credits"
                      >
                        <Sparkles className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleResetCredits(restaurant.id)}
                        title="Reset Credits Usage"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredRestaurants.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            No restaurants found matching your filters
          </div>
        )}
      </div>

      {/* Edit Subscription Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ ...editDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Subscription</DialogTitle>
            <DialogDescription>
              Update subscription for {editDialog.restaurant?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select value={editForm.plan} onValueChange={(v) => setEditForm({ ...editForm, plan: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter (£25/mo)</SelectItem>
                  <SelectItem value="professional">Professional (£50/mo)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (Custom)</SelectItem>
                  <SelectItem value="demo">Demo Account (Free)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={editForm.expires_at}
                onChange={(e) => setEditForm({ ...editForm, expires_at: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                placeholder="Admin notes..."
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, restaurant: null })}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSubscription}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend Subscription Dialog */}
      <Dialog open={extendDialog.open} onOpenChange={(open) => setExtendDialog({ ...extendDialog, open })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Extend Subscription</DialogTitle>
            <DialogDescription>
              Add days to {extendDialog.restaurant?.name}'s subscription
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Days to Add</Label>
              <div className="flex gap-2">
                {[7, 14, 30, 60, 90, 365].map((days) => (
                  <Button
                    key={days}
                    size="sm"
                    variant={extendDays === days ? 'default' : 'outline'}
                    onClick={() => setExtendDays(days)}
                  >
                    {days}
                  </Button>
                ))}
              </div>
              <Input
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                className="mt-2"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendDialog({ open: false, restaurant: null })}>
              Cancel
            </Button>
            <Button onClick={handleExtendSubscription}>
              Extend by {extendDays} Days
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Credits Dialog */}
      <Dialog open={creditsDialog.open} onOpenChange={(open) => setCreditsDialog({ ...creditsDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage AI Credits</DialogTitle>
            <DialogDescription>
              Update AI credits for {creditsDialog.restaurant?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Credits Display */}
            {creditsDialog.restaurant && (
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">
                      {creditsDialog.restaurant.ai_credits?.limit || 2}
                    </p>
                    <p className="text-xs text-slate-500">Monthly Limit</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-purple-600">
                      {creditsDialog.restaurant.ai_credits?.purchased || 0}
                    </p>
                    <p className="text-xs text-slate-500">Purchased</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">
                      {creditsDialog.restaurant.ai_credits?.used || 0}
                    </p>
                    <p className="text-xs text-slate-500">Used</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={creditsForm.action} onValueChange={(v) => setCreditsForm({ ...creditsForm, action: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add Credits</SelectItem>
                  <SelectItem value="remove">Remove Credits</SelectItem>
                  <SelectItem value="set">Set Credits (Exact)</SelectItem>
                  <SelectItem value="set_limit">Set Monthly Limit</SelectItem>
                  <SelectItem value="reset_used">Reset Used to 0</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {creditsForm.action !== 'reset_used' && (
              <div className="space-y-2">
                <Label>Amount</Label>
                <div className="flex gap-2">
                  {[5, 10, 25, 50, 100].map((amt) => (
                    <Button
                      key={amt}
                      size="sm"
                      variant={creditsForm.amount === amt ? 'default' : 'outline'}
                      onClick={() => setCreditsForm({ ...creditsForm, amount: amt })}
                    >
                      {amt}
                    </Button>
                  ))}
                </div>
                <Input
                  type="number"
                  value={creditsForm.amount}
                  onChange={(e) => setCreditsForm({ ...creditsForm, amount: parseInt(e.target.value) || 0 })}
                  className="mt-2"
                />
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input
                placeholder="e.g., Demo account setup, Compensation..."
                value={creditsForm.reason}
                onChange={(e) => setCreditsForm({ ...creditsForm, reason: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditsDialog({ open: false, restaurant: null })}>
              Cancel
            </Button>
            <Button onClick={handleUpdateCredits}>
              {creditsForm.action === 'add' && <Plus className="w-4 h-4 mr-2" />}
              {creditsForm.action === 'remove' && <Minus className="w-4 h-4 mr-2" />}
              Apply Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
